import { useState, useEffect, useRef, useContext } from "react";
import { Box, Spinner, Text } from "grommet";
import { useLocation } from "react-router-dom";
import { useFetch } from "use-http";
import PreHarvestEvaluation from "../components/PreHarvest/PreHarvestEvaluation";
import HomePageNav from "../components/Base components/HomePageNav";
import SheetHeader from "../components/Base components/SheetHeader";
import Notification from "../components/Base components/Notification";
import { getDBURL, updateStateHandler, convertDateToDisplayformat, correctTimeZone, calculateHeight, 
            sheetStatus, getKeyByValue, generateSyncId } from "../components/Utils/Utils";
import WarningPopupWindow from "../components/Base components/WarningPopupWindow";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../App";
import { useErrorLogger } from '../components/Base components/Logger';

const PreHarvestPage = () => {
    const currentUser = useContext(UserContext);
    const { state } = useLocation();
    const { DBlogError } = useErrorLogger();
    // if the 'evaluation' is new (just now created), 
    // then editMode is false, otherwise true
    const [editMode, setEditMode] = useState(state.editMode);
    const [disable, setDisable] = useState(state.disable);
    const [plantInfo, setPlantInfo] = useState(state.plantInfo);
    const treatmentId = state.treatmentId;
    // console.log('in PreHarvestPage treatmentId  >> ', state.treatmentId);
    const station = state.station;
    const [loadExisting, setLoadExisting] = useState(false);
    let existingPreharvestID = state.preharvestID;

    const newEvaluation = useRef(state.preharvestID === null);
    // first when the page is only opened and the preharvest values 
    // were not changed  is false, after the first change is true (must be saved)
    const [hasEdit, setHasEdit] = useState(false); 
    const sheetName = state.sheetName ? state.sheetName : "EVALUATION";
 
    // console.log('In PreHarvestPage state >> ', state);

    const getInitialState = () => {
        // console.log(plantInfo);
        setPreHarvestValues({
            PreHarvestID: generateSyncId(),
            SUID: plantInfo !== null && plantInfo.IsSeedling === 1 ? plantInfo.SUID : null,
            SeedlingID: plantInfo !== null && plantInfo.IsSeedling === 1 ? plantInfo.Id : null,         
            Year: new Date().getFullYear(),
            TreatmentId: treatmentId !== undefined ? treatmentId : null,
            StationId: station ? station.Id : null,
            Grade: 0,  
            CreateDate: correctTimeZone(new Date()),
            CreatedBy: currentUser.email,                        
            StatusID: getKeyByValue(sheetStatus,'Completed'),    
            TreatmentPlan: null,                    
            TED: plantInfo === undefined ? true : false, 
            Pruning: null,
            Fertility: null,
            Pathology: null,
            Rotten: null,
            ColorUniformity: null,
            ShotBerries: null,
            WaterBerries: null,
            BunchDensity: null,
            BunchShapeSC: null,
            BunchShapeCS: null,
            BunchShapeLC: null,
            BunchShapeC: null,
            BunchShapeWC: null,
            BunchShapeWD: null,
            Rudiment: null,
            Seeded: null,
            OverallAppeal: null,
            BerryTexture: null,
            Skin: null,
            TSSAtEval: null,
            AcidAtEval: null,
            TSSAtRipeness: null,
            PHAtRipeness: null,
            TAAtRipeness: null,
            AvgBerryWeight: null,
            AvgBerryDiameter: null,
            AvgBerryArea: null,
            AvgBerryFirmness: null,
            Stem: null,
            BerrySize: null,
            Attachment: null,
            Astringency: null,
            Flavor: null,
            EatingQuality: null,
            SkinStructure: null,
            CapStem: null,
            Brush: null,
            BerryShapeC: null,  
            BerryShapeE: null,
            BerryShapeO: null,
            BerryShapeOv: null,
            BerryShapeR: null,          
            Notes: null,
            BunchImgExist: null,
            BerryImgExist: null,
            HalvedBerryImgExist: null,
            FreeImgText: "",
            FreeImgExist: null,
            EvaluationProgress: 0,
            FinishedAt: null,
            FinishedBy: null,
            LastSyncDate: correctTimeZone(new Date())
        });

        if (isFirstRender.current) {
            isFirstRender.current = false; // Mark first render as handled
        } 
    };
    const [preHarvestValues, setPreHarvestValues] = useState({});

    const [seeded, setSeeded] = useState(false);
    const [success, setSuccess] = useState(false);
    const [navigateAfterSuccess, setNavigateAfterSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");
 
    const [showWarning, setShowWarning] = useState(false);
    const [warningMessage, setWarningMessage] = useState(''); 

    const [grade, setGrade] = useState(0);
    const [evProgress, setEvProgress] = useState(0);
    const navigate = useNavigate();

    const { get, post, loading, error } = useFetch(getDBURL());

    // Usage of useRef to store previous values
    const isFirstRender = useRef(true);
    const prevPreHarvestValues = useRef(preHarvestValues);

    //effect to run on each preHarvestValues change
    useEffect(() => {

        if (isFirstRender.current) {
            return; // Skip the effect on initial load
        }

        // Reset notifications
        setShowError(false);
        setSuccess(false);
        setNotificationMessage("");

        // Function to check if two objects are different, excluding specific keys
        const haveValuesChanged = (prev, current) => {
            if (prev && Object.keys(prev).length === 0)
                return false;

            // Clone objects without the ignored keys
            const filterKeys = (obj) => {
                const { BunchImgExist, BerryImgExist, HalvedBerryImgExist, FreeImgExist, ...filtered } = obj;
                return filtered;
            };

            return JSON.stringify(filterKeys(prev)) !== JSON.stringify(filterKeys(current));
        };

        // Check if prevPreHarvestValues have changed
       const preHarvestValuesChanged = haveValuesChanged(prevPreHarvestValues.current, preHarvestValues) && !isFirstRender.current ;
    
        //if (editMode && preHarvestValuesChanged) {
        if (preHarvestValuesChanged) {
            // console.log('hasEdit  before changing  >> ', hasEdit);
            // console.log('Setting hasEdit to true'); // Debugging log
            setHasEdit(true);
        }
    
        // Update previous values
        prevPreHarvestValues.current = preHarvestValues;

    }, [preHarvestValues]);

    useEffect(() => {
        if (preHarvestValues.Grade !== grade) {
            updateStateHandler(setPreHarvestValues, "Grade", grade);
        }
    }, [grade]);
 
    useEffect(() => {
        if (preHarvestValues.EvaluationProgress !== evProgress) {
            updateStateHandler(setPreHarvestValues, "EvaluationProgress", evProgress);
        }
       
    }, [evProgress]);

    useEffect(() => {
        if (hasEdit) {
            updateStateHandler(setPreHarvestValues, "UpdatedAt", correctTimeZone(new Date()));
            updateStateHandler(setPreHarvestValues, "UpdatedBy", currentUser.email); 
            updateStateHandler(setPreHarvestValues, "LastSyncDate", correctTimeZone(new Date()));   
        }       
    }, [hasEdit]);    


    const updateGrade = (prevOption, newOption) => {
        // Set default values if prevOption or newOption are null
        const prevGrade = prevOption?.grade || 0;
        const newGrade = newOption?.grade || 0;
        const prevIndex = prevOption?.index || 0;
        const newIndex = newOption?.index || 0;

        setGrade((prevGradeState) => prevGradeState - prevGrade + newGrade);
        const progressStep = station ? 3.226 : 4.348;

        // Update evProgress
        if (prevIndex === 0 && newIndex !== 0) {
            setEvProgress((prevEvProgress) => prevEvProgress + progressStep);
            // console.log(`In updateGrade: evProgress + progressStep: ${evProgress} + ${progressStep} = ${evProgress+progressStep}`);

        } else if (prevIndex !== 0 && newIndex === 0) {
            setEvProgress((prevEvProgress) => prevEvProgress - progressStep);
            // console.log(`In updateGrade: evProgress - progressStep: ${evProgress} - ${progressStep} = ${evProgress-progressStep}`);

        }
        //  console.log("grade:" + (grade - prevOption.grade + newOption.grade));
        //  console.log("evProgress:", evProgress);
    };

    const updateProgressByCheck = (prevOption, newOption) => {
        if (prevOption === newOption) return;

        const progressStep = station ? 3.226 : 4.348;

        if (prevOption === 0 && newOption > 0) {
            setEvProgress(prevEvProgress => (prevEvProgress + progressStep));
            // console.log(`In updateProgress: evProgress + progressStep: ${evProgress} + ${progressStep} = ${evProgress+progressStep}`);
        } else if (prevOption > 0 && newOption === 0) {
            setEvProgress(prevEvProgress => (prevEvProgress - progressStep));
            // console.log(`In updateProgress: evProgress - progressStep: ${evProgress} - ${progressStep} = ${evProgress-progressStep}`);
        }
    
        // console.log("result evProgress:", evProgress);
    };
    

    useEffect(() => {
        if (editMode) {
            setLoadExisting(true);

            // used for loading existing data!!!
            ( async function loadPreHarvestHandler() {
                try {
                    // console.log('existingPreharvestID in loadPreHarvestHandler   ', existingPreharvestID);
                    if (!existingPreharvestID)
                        return;

                    const response = await get(`/loadPreHarvestValues/preharvest_ID=${existingPreharvestID}/dt=` + Date.now() );

                    // Check if the response is successful
                    if (!response.success) {
                        // console.log("In loadPreHarvestHandler response   >> ", response.message); 
                        throw new Error(`HTTP error! ${response.message}`);
                    } 
                    
                    const data = response.result;
                    // console.log("loadPreHarvestHandler result:", data);
                    mapDBValues(data[0]);
                }
                catch(error)
                {
                    console.error('Error in loadPreHarvestHandler: ', error.message);
                    setShowError(true);
                    setNotificationMessage(error.message);

                    // Log the error
                    await DBlogError({
                        error: error,
                        parameters: JSON.stringify({ existingPreharvestID }),
                        userName: currentUser.email,
                        moduleName: 'PreHarvestPage',
                        functionName: 'loadPreHarvestHandler',
                        description: 'Error occurred in loadPreHarvestHandler function'
                    });

                };  
            })();
        }
        else
            getInitialState();
    }, []);
     
    const navigateToEvalSheet = () => {
        setNavigateAfterSuccess(false); // reset flag
        // console.log(' submit  plantInfo:  ', plantInfo);
        // console.log(' submit  props.plantInfo.IsSeedling:  ', props.plantInfo.IsSeedling);
        // console.log(' submit   props.seedlingId:  ',  props.plantInfo.Id);
        // console.log(' submit   props.seedlingYear:  ',  props.plantInfo.Year);
        // console.log(' submit   state.seedlingId:  ',  state.seedlingId);
        // console.log(' submit   state.seedlingYear:  ',  state.seedlingYear);
        // console.log(' submit   state.stationNum:  ',  state.stationNum);
        // console.log(' PreharvestPage submit   state:  ',  state);
        //  console.log(' submit   state.selectedField:  ',  state.selectedField);
        // console.log(' submit  props.plantInfo.IsSeedling:  ', props.plantInfo.IsSeedling);  

        navigate('/eval-sheet', { 
            state:{
                fromAnotherScreen: true, 
                newExist: "ExistingEvaluation", 
                plantInfo: plantInfo,         // for EvaluationSheet screen 
                chosenOption: plantInfo !== null && plantInfo.IsSeedling === 1 ? "Seedlings" : "Stations",
                seedlingId: state.seedlingId,
                seedlingYear: state.seedlingYear,
                seedlingsArr: state.seedlingsArr || [],
                seedlingRow: state.seedlingRow,
                selectedField: state.selectedField,
                selectedStation: plantInfo !== null && plantInfo.IsSeedling === 1 ? { Id: '00000000-0000-0000-0000-000000000000', Name: ''} : state.selectedStation,
                activeStationsList: state.activeStationsList !== null && state.activeStationsList !== undefined ? state.activeStationsList : [],
                chosenVineInfo: plantInfo,    // for ExistingEvaluation screen               
                showSeedlingOnField: plantInfo !== null && plantInfo.IsSeedling === 1 ? true : false,
                showScionOnField:  false,
                showTreatments: plantInfo !== null && plantInfo.IsSeedling === 1 ? false : true,
                showTreatmentsMultiYearSummary: state.showTreatmentsMultiYearSummary,
            }
        });
    };


   const mapDBValues = (values) => {
        if (!values)
            return;

        // console.log(' in mapDBValues >> ', values);
        setPreHarvestValues({
            PreHarvestID: values.Id,
            SeedlingID: values.SeedlingId,  
            TreatmentId: values.TreatmentId, 
            StationId: values.StationId, 
            Year: values.Year,
            Grade: values.Grade, 
            CreateDate: values.Create_Date, 
            CreatedBy: values.CreatedBy, 
            StatusID: values.StatusId, 
            TreatmentPlan: values.TreatmentPlan,                       
            Pruning: values.Pruning,
            Fertility: values.Fertility,
            Pathology: values.Pathology,
            Rotten: values.Rotten,
            ColorUniformity: values.Color_Uniformity,
            ShotBerries: values.Shot_Berries,
            WaterBerries: values.Water_Berries,
            BunchDensity: values.Bunch_Density,
            BunchShapeSC: values.Short_Conical,
            BunchShapeCS: values.Conical_Shouldered,
            BunchShapeLC: values.Long_Conical,
            BunchShapeC: values.Cylindrical_Bunch,
            BunchShapeWC: values.Winged_Cylindrical,
            BunchShapeWD: values.Winged_Double,
            Rudiment: values.Rudiment,
            Seeded: values.Seeded,
            OverallAppeal: values.Overall_Appeal,
            BerryTexture: values.Berry_texture,
            Skin: values.Skin,
            TSSAtEval: values.TSS_At_Eval == null ? 0 : values.TSS_At_Eval,
            AcidAtEval: values.Acid_At_Eval,
            TSSAtRipeness: values.TSS_At_Ripeness == null ? 0 : values.TSS_At_Ripeness,
            PHAtRipeness: values.PH_At_Ripeness == null ? 0 : values.PH_At_Ripeness,
            TAAtRipeness: values.TA_At_Ripeness == null ? 0 : values.TA_At_Ripeness,
            AvgBerryWeight: values.Avg_Berry_Weight == null ? 0 : values.Avg_Berry_Weight,
            AvgBerryDiameter: values.Avg_Berry_Diameter == null ? 0 : values.Avg_Berry_Diameter,
            AvgBerryArea:  values.Avg_Berry_Area == null ? 0 : values.Avg_Berry_Area,
            AvgBerryFirmness:  values.Avg_Berry_Firmness == null ? 0 : values.Avg_Berry_Firmness,
            Stem: values.Stem,
            BerrySize: values.Berry_Size_Uniformity,            
            Attachment: values.Attachment,
            Astringency: values.Astringency,
            Flavor: values.Flavor,
            EatingQuality: values.Eating_Quality,
            SkinStructure: values.Skin_Structure,
            CapStem: values.Cap_Stem,
            Brush: values.Brush,
            BerryShapeC: values.Cylindrical_Berry,  
            BerryShapeE: values.Ellipsoid,
            BerryShapeO: values.Obovoid,
            BerryShapeOv: values.Ovoid,
            BerryShapeR: values.Round,              
            Notes: values.Notes,
            BunchImgExist: values.BunchImgExist,
            BerryImgExist: values.BerryImgExist,
            HalvedBerryImgExist: values.HalvedBerryImgExist,
            FreeImgText: values.FreeImgText,
            FreeImgExist: values.FreeImgExist,
            EvaluationProgress: values.EvaluationProgress === null ? 0 : values.EvaluationProgress,
            UpdatedAt: values.UpdatedAt,
            UpdatedBy: values.UpdatedBy,
            FinishedAt: values.FinishedAt,
            FinishedBy: values.FinishedBy,     
            LastSyncDate: values.LastSyncDate,     
        });
        // setPreharvestStatus(values.StatusID);
        setGrade(values.Grade);
        setEvProgress(values.EvaluationProgress);
        setLoadExisting(false);
        if (isFirstRender.current) {
            isFirstRender.current = false; // Mark first render as handled
        }   
    };

    // console.log('preHarvestValues  ',preHarvestValues);
    

    async function existEvaluationBySeedlingSpecificDate(seedlingId, date) {
        try {
            if (seedlingId === null || seedlingId === undefined || date === '') 
                return;

            // Fetch evaluation data for the given seedling ID and date
            const response = await get(`/getEvaluationBySeedlingCreateDate/seedling_id=${seedlingId}/create_date=${date}/dt=${Date.now()}`);

            // Check if the response is successful
            if (!response.success) {
                throw new Error(`HTTP error! ${response.message}`);
            }
    
            const res = response.result;
            if( res !== undefined && res > 0)
            {      
                // The evaluation for a given seedling in given date already exists    
                return true;
            }
            return false;
        }
        catch(error)
        {
            console.error('Error in existEvaluationForSpecificDate: ', error.message);
            setShowError(true);
            setNotificationMessage(error.message);

            // Log the error
            await DBlogError({
                error: error,
                parameters: JSON.stringify({seedlingId: seedlingId, date: date}),
                userName: currentUser.email,
                moduleName: 'PreHarvestPage',
                functionName: 'existEvaluationBySeedlingSpecificDate',
                description: 'Error in existEvaluationBySeedlingSpecificDate function'
            });
        }  
    };

    const validInput = async () =>
    {
        if ( preHarvestValues.Year === null || preHarvestValues.Year === "") {
            setWarningMessage("The year of evaluation is required.");
            setShowWarning(true);
            return false;              
        }

       const pattern = /^\d+$/; // Regular expression pattern for positive integers
       // const pattern = /^0*[1-9]\d*$/;
  
        if (!pattern.test(preHarvestValues.Year)) {
            setWarningMessage("The year must be positive integer number.");
            setShowWarning(true);
            return false;      
        }

        const num = Number(preHarvestValues.Year);
        if(num.toString().length !== 4)
        {
            setWarningMessage("Please enter a valid 4-digit year value for the new evaluation.");
            setShowWarning(true);
            return false;               
        } 

       const isExist = await existEvaluationBySeedlingSpecificDate(preHarvestValues.SeedlingID, preHarvestValues.CreateDate); 
        // console.log('preHarvestValues In valid input f-n: ', preHarvestValues);
        // console.log('preHarvestValues.PreHarvestID In valid input f-n: ', preHarvestValues.PreHarvestID);
        // console.log('preHarvestValues.CreateDate In valid input f-n: ', preHarvestValues.CreateDate);
        // console.log('isExist In valid input f-n: ', isExist);
               
        if (isExist) {
            setWarningMessage('The evaluation for the given ' + convertDateToDisplayformat(preHarvestValues.CreateDate) + ' date already exists');
            setShowWarning(true);
            return false;              
        }

        return true;
    };

    async function savePreHarvestHandler() {
        // console.log(' In savePreHarvestHandler !! level 1');
        // console.log("In savePreHarvestHandler treatmentValues   >> ", treatmentValues);

        // create or update depending on edit mode:
        if (editMode && !newEvaluation.current) {
            try {
                const response = await post("/updatePreharvest", preHarvestValues);

                // Check if the response is successful
                if (!response.success) {
                    // console.log("In savePreHarvestHandler response   >> ", response.message); 
                    throw new Error(`HTTP error! ${response.message}`);
                }

                const res = response.result;
                // console.log('res in savePreHarvestHandler f-n updating >> ', res);
                // If there was an update returns 1, otherwise returns 0
                if (res === 1) {
                    updateBestTreatmentOnStation(preHarvestValues.PreHarvestID, preHarvestValues.Year);
                    setNotificationMessage(sheetName + " sheet saved successfully.");
                    setSuccess(true);
                    setHasEdit(false);
                    return true;
                }
            }
            catch(error) {
                console.error('Error In savePreHarvestHandler f-n: ', error.message);
                setShowError(true);
                setNotificationMessage('The update preharvest was failed!!');

                // Log the error
                await DBlogError({
                    error: error,
                    parameters: JSON.stringify(preHarvestValues),
                    userName: currentUser.email,
                    moduleName: 'PreHarvestPage',
                    functionName: 'savePreHarvestHandler',
                    description: 'Error in savePreHarvestHandler function at update Preharvest values, in \'if\' part when editMode === true and changeCount.current = 4'
                });

                return false;
            }       
        } else {
            try {
                const isValid = await validInput();
                if (!isValid) {
                    return false;
                } else { 
                    const response = await post("/createPreharvest", preHarvestValues);
                    // console.log('response in savePreHarvestHandler f-n creating >> ', response);

                    // Check if the response is successful
                    if (!response.success) {
                        throw new Error(`HTTP error! ${response.message}`);
                    }
            
                    const res = response.result;
                    // console.log('res in savePreHarvestHandler f-n creating >> ', res);

                    setNotificationMessage(sheetName + " sheet created successfully.");
                    setSuccess(true);
                    setEditMode(true); 
                    updateBestTreatmentOnStation(res, preHarvestValues.Year);
                    updateStateHandler(setPreHarvestValues, "PreHarvestID", res); 
                    setHasEdit(false);
                    newEvaluation.current = false;
                    return true;
                }
            }
            catch(error) {
                console.error(' 🔴 Error in savePreHarvestHandler >> ', error.message)
                setShowError(true);
                setNotificationMessage('The create preharvest was failed!!');

                // Log the error
                await DBlogError({
                    error: error,
                    parameters: JSON.stringify(preHarvestValues),
                    userName: currentUser.email,
                    moduleName: 'PreHarvestPage',
                    functionName: 'savePreHarvestHandler',
                    description: 'Error in savePreHarvestHandler function at create Preharvest values, in \'else\' part when changeCount.current = 2'
                });

                return false;
            }
        }
    };

    const updateBestTreatmentOnStation = async(evaluationId, evaluationYear) => {
        // console.log('PreHarvest Page in updateBestTreatmentOnStation !!!  ');
        if (plantInfo || !station ||  treatmentId === undefined )       
            return;

        // console.log('station id >>>  ', station.Id);
        // console.log('station >>>  ', station);
        // console.log('treatment id >>>  ', treatmentId);
        // console.log('evaluationYear >>>  ', evaluationYear);

         const updateEvalValues = {
            StationId: station.Id,
            TreatmentId: treatmentId,
            EvaluationId: 'FFFFFFFF-FFFF-FFFF-FFFF-FFFFFFFFFFFF',
            Year: new Date().getFullYear(),
            PerformedAt: correctTimeZone(new Date()),
            PerformedBy: 'auto',
            // LastSyncDate: correctTimeZone(new Date()),
        };

        let response = await post(`/updateBestEvaluationForTreatment/dt=${Date.now()}`, updateEvalValues);
        console.log('Best evaluation updated auto response.result:', response.result);            

        if (!response.success) {
            throw new Error(`${response.message}`);
        }

        const updateTreatmentValues = {
            Id: generateSyncId(),
            StationId: station.Id,
            BestTreatmentYear: new Date().getFullYear(),
            // Compute the BestTreatmentId as the highest-graded treatment from VEM_Evaluations.
            BestTreatmentId: 'FFFFFFFF-FFFF-FFFF-FFFF-FFFFFFFFFFFF',
            BestTreatmentDate: correctTimeZone(new Date()),
            BestTreatmentBy: 'auto',
        };
        
        response = await post(`/updateStationBestTreatment/dt=${Date.now()}`, updateTreatmentValues);

        if (!response.success) {
            throw new Error(`${response.message}`);
        }
        // console.log('treatment updated number response.result:', response.result);
       
        // Handling response from updateStationBestTreatment
        const { returnValue, BestTreatmentId } = response.result; // Assuming the response is structured like this
        // console.log(`Treatment update status: ${returnValue}, BestTreatmentId: ${BestTreatmentId}`);
    };

    // hides the "Warning" popup window 
    const onClose = () => {
        setWarningMessage('');
        setShowWarning(false);
    };

    // console.log('preHarvestValues >> ', preHarvestValues)
    // console.log('preHarvestValues create date >> ', preHarvestValues.CreateDate)
    // console.log('In PreHarvest page state.selectedField >> ', state.selectedField)
    // console.log('In PreHarvest page state >> ', state)

    return (
        <Box >
            <Box 
                style={{ 
                    position: 'fixed',
                    top: 0,
                    zIndex: 10,
                    height: calculateHeight(5)
                }} >
                <HomePageNav 
                    selectedField = {state.selectedField}
                    station = {state.station}
                    plantInfo={plantInfo} 
                    seedlingId={state.seedlingId}
                    seedlingYear={state.seedlingYear}
                    seedlingRow={state.seedlingRow}
                    seedlingsArr={state.seedlingsArr}
                    activeStationsList={state.activeStationsList}
                    hasEdit={hasEdit} 
                    save={savePreHarvestHandler}
                    showTreatments={state.showTreatments}
                    showEvaluationForTreatment={state.showEvaluationForTreatment}
                    showTreatmentsMultiYearSummary={state.showTreatmentsMultiYearSummary}
                />
            </Box>
            <Box>
                <SheetHeader
                    plantInfo={plantInfo}
                    sheetName={plantInfo !== null && plantInfo.IsSeedling === 1 ? 'Evaluation' : 'TREATMENT EVALUATION DATA'}
                    setSheetValues={setPreHarvestValues}
                    sheetYear={preHarvestValues.Year === undefined ? new Date().getFullYear() : preHarvestValues.Year}
                    sheetDate={preHarvestValues.CreateDate === undefined ? convertDateToDisplayformat(new Date()) : convertDateToDisplayformat(preHarvestValues.CreateDate)}
                    editMode={editMode} // true for existing evaluation and false only for new evaluation
                    isStation={plantInfo !== null && plantInfo.IsSeedling === 1 ? false : true}
                    station = {state.station}
                    selectedField = {state.selectedField}
                />
            </Box>

            {/* Preharvest Sheet */}
            <Box>
                {showWarning && (
                <WarningPopupWindow msg={warningMessage} close={onClose}/>
                )}
                {showError && (
                    <Notification
                    status="critical"
                    message={`ERROR SAVING ${sheetName}.`}
                    onClose={() => setShowError(false)}
                  />
                )}
                
                {success && (
                    <Notification
                    status="normal"
                    message={`${sheetName} SHEET SAVED SUCCESSFULLY.`}
                    onClose={() => {
                        setSuccess(false);
                        if(navigateAfterSuccess) // <-- trigger navigation here
                            navigateToEvalSheet(true); 
                      }}
                  />                  
                )}
                {loading && loadExisting && (
                    <Box align="center" justify="center" direction="row" gap="large" pad="small">
                    <Spinner /> <Text>Loading...</Text>
                    </Box>
                )}
                {!loadExisting && (
                    <PreHarvestEvaluation
                    preHarvestValues={preHarvestValues}
                    setPreHarvestValues={setPreHarvestValues}
                    savePreHarvestHandler={savePreHarvestHandler}
                    updateGrade={updateGrade}
                    updateProgressByCheck={updateProgressByCheck}
                    setSeeded={setSeeded}
                    grade={grade}
                    setGrade={setGrade}
                    setSuccess={setSuccess}
                    setNavigateAfterSuccess={setNavigateAfterSuccess}
                    setShowError={setShowError}
                    editMode={editMode}
                    disable={disable}
                    plantInfo={plantInfo}
                    station={station}
                    setEditMode={setEditMode}
                    sheetName={sheetName}
                    />
                )}
            </Box>
        </Box>
    
    );
};

export default PreHarvestPage;

